import { Typography } from '@mui/material';

import { useEffect, useState } from 'react';

import { useSelector, useDispatch } from 'react-redux';

import { getFilterHeaderValues } from 'common/api';
import { IFilterKeysResponse } from 'common/api/type-def';
import { ReactComponent as FilterIcon } from 'common/assets/images/Filter.svg';
import ButtonComp from 'common/components/Button/ButtonComp';
import AdvancedFilters, {
	IAdvancedFiltersOptions,
	IAdvancedFilterValueOptions,
} from 'common/components/Filters/AdvancedFilters';
import SideDrawer from 'common/components/SideDrawer/SideDrawer';
import { FILTER_LABELS } from 'common/constants';
import {
	workFlowStatusInfo,
	priorityList,
	sourceList,
	FILTER_TYPE,
	FILTER_NAME,
} from 'common/constants/CitationDetails';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import {
	REG_INV_GROUP_SEARCH_MAP,
	REG_INV_REGULATION_SEARCH_MAP,
} from 'common/constants/RegulationInventoryConstants';
import { API_FAIL_ERROR_MESSAGE, PWC } from 'common/constants/TextContent';
import { RootState } from 'common/store/AppStore';
import {
	setFileDrawer,
	setToastMessage,
} from 'common/store/reducer/commonReducer';
import {
	setRegInventoryAppliedFilters,
	setShowAssignedToMeChecked,
} from 'common/store/reducer/regulationInventoryReducer';
import { filterDataHandler } from 'common/utils/utilFunctions';

import { getFilterItemList } from 'features/RegulationInventory/api';
import {
	IRegInventoryFilterHeaders,
	IFilterState,
	IRegInventoryFilterResponse,
} from 'features/RegulationInventory/api/type-def';

import styles from './AdvancedFilter.module.scss';

const { INTERNATIONA_NAME, FEDERAL_STATES_NAME } = FILTER_NAME;

const {
	THEME_TYPE,
	REG_SECTION_TYPE,
	JURISDICTION_TYPE,
	ISSUANCE_TYPE,
	WORK_FLOW_STATUS_TYPE,
	OWNER_TYPE,
	CREATED_BY_TYPE,
	PRIORITY_TYPE,
	INTERNATIONA_TYPE,
	REG_BOOK_TYPE,
	DISPLAY_ID_TYPE,
	SOURCE_TYPE,
	FEDERAL_STATES_TYPE,
} = FILTER_TYPE;

const { JURISDICTION } = REG_INV_REGULATION_SEARCH_MAP;
const { JURISDICTION_GROUP } = REG_INV_GROUP_SEARCH_MAP;

const { OK, FORBIDDEN, INTERNAL_SERVER_ERROR, BAD_REQUEST } = HTTP_STATUS_CODES;

const { ADVANCED_FILTERS } = FILTER_LABELS;

interface IAdvancedFilterProps {
	applyFilterClicked: any;
}

const defaultOptionsList: IFilterState = {
	themeName: {
		loading: false,
		data: [],
		type: THEME_TYPE,
		name: '',
		searchText: '',
		searchFlag: false,
	},
	sectionName: {
		loading: false,
		data: [],
		type: REG_SECTION_TYPE,
		name: '',
		searchText: '',
		searchFlag: false,
	},
	international: {
		loading: false,
		data: [],
		type: INTERNATIONA_TYPE,
		name: INTERNATIONA_NAME,
		searchText: '',
		searchFlag: false,
	},
	unitedStates: {
		loading: false,
		data: [],
		type: FEDERAL_STATES_TYPE,
		name: FEDERAL_STATES_NAME,
		searchText: '',
		searchFlag: false,
	},
	assignedUserName: {
		loading: false,
		data: [],
		type: OWNER_TYPE,
		name: '',
		searchText: '',
		searchFlag: false,
	},
	createdByName: {
		loading: false,
		data: [],
		type: CREATED_BY_TYPE,
		name: '',
		searchText: '',
		searchFlag: false,
	},
	issuanceType: {
		loading: false,
		data: [],
		type: ISSUANCE_TYPE,
		name: '',
		searchText: '',
		searchFlag: false,
	},
	bookTitle: {
		loading: false,
		data: [],
		type: REG_BOOK_TYPE,
		name: '',
		searchFlag: false,
	},
	status: {
		loading: false,
		data: workFlowStatusInfo,
		type: WORK_FLOW_STATUS_TYPE,
		name: '',
		searchFlag: false,
	},
	priority: {
		loading: false,
		data: priorityList,
		type: PRIORITY_TYPE,
		name: '',
		searchFlag: false,
	},
	source: {
		loading: false,
		data: sourceList,
		type: SOURCE_TYPE,
		name: '',
		searchFlag: false,
	},
	jurisdiction: {
		loading: false,
		data: [],
		type: JURISDICTION_TYPE,
		name: '',
		searchFlag: false,
	},
};

const AdvancedFilter = (filterProps: IAdvancedFilterProps) => {
	const { applyFilterClicked } = filterProps;

	const regInvAppliedFilters = useSelector(
		(state: RootState) => state.regulationInventory.regInventoryAppliedFilters,
	);

	const showAssignedMeChecked = useSelector(
		(state: RootState) => state.regulationInventory.showAssignedToMeChecked,
	);

	const loggedInUser = useSelector(
		(state: RootState) => state.common.loggedInUser,
	);

	const fileDrawerState = useSelector(
		(state: RootState) => state.common.fileDrawer,
	);

	const dispatch = useDispatch();

	const { buttonTextColor, buttonTextColorApplied } = styles;

	const [optionsList, setOptionsList] =
		useState<IFilterState>(defaultOptionsList);

	const [isAdvancedFilterOpen, setIsAdvancedFilterOpen] =
		useState<boolean>(false);

	const [isFilterValuesLoading, setIsFilterValuesLoading] =
		useState<boolean>(false);

	const setLoadingStates = (loading: boolean, type: string, name: string) => {
		switch (type) {
			case REG_BOOK_TYPE:
				setOptionsList((prev) => ({
					...prev,
					bookTitle: {
						...prev.bookTitle,
						loading,
					},
				}));
				break;
			case THEME_TYPE:
				setOptionsList((prev) => ({
					...prev,
					themeName: {
						...prev.themeName,
						loading,
					},
				}));
				break;
			case REG_SECTION_TYPE:
				setOptionsList((prev) => ({
					...prev,
					sectionName: {
						...prev.sectionName,
						loading,
					},
				}));
				break;
			case INTERNATIONA_TYPE:
				if (name === INTERNATIONA_NAME) {
					setOptionsList((prev) => ({
						...prev,
						international: {
							...prev.international,
							loading,
						},
					}));
				} else {
					setOptionsList((prev) => ({
						...prev,
						unitedStates: {
							...prev.unitedStates,
							loading,
						},
					}));
				}

				break;
			case CREATED_BY_TYPE:
				setOptionsList((prev) => ({
					...prev,
					createdByName: {
						...prev.createdByName,
						loading,
					},
				}));
				break;
			case ISSUANCE_TYPE:
				setOptionsList((prev) => ({
					...prev,
					issuanceType: {
						...prev.issuanceType,
						loading,
					},
				}));
				break;
			case OWNER_TYPE:
				setOptionsList((prev) => ({
					...prev,
					assignedUserName: {
						...prev.assignedUserName,
						loading,
					},
				}));
				break;
			default:
				break;
		}
	};

	const setSearchFieldTexts = (
		type: string,
		name: string,
		searchText: string,
	) => {
		switch (type) {
			case REG_BOOK_TYPE:
				setOptionsList((prev) => ({
					...prev,
					bookTitle: {
						...prev.bookTitle,
						searchText,
					},
				}));
				break;
			case THEME_TYPE:
				setOptionsList((prev) => ({
					...prev,
					themeName: {
						...prev.themeName,
						searchText,
					},
				}));
				break;
			case REG_SECTION_TYPE:
				setOptionsList((prev) => ({
					...prev,
					sectionName: {
						...prev.sectionName,
						searchText,
					},
				}));
				break;
			case INTERNATIONA_TYPE:
				if (name === INTERNATIONA_NAME) {
					setOptionsList((prev) => ({
						...prev,
						international: {
							...prev.international,
							searchText,
						},
					}));
				} else {
					setOptionsList((prev) => ({
						...prev,
						unitedStates: {
							...prev.unitedStates,
							searchText,
						},
					}));
				}
				break;
			case OWNER_TYPE:
				setOptionsList((prev) => ({
					...prev,
					assignedUserName: {
						...prev.assignedUserName,
						searchText,
					},
				}));
				break;
			case CREATED_BY_TYPE:
				setOptionsList((prev) => ({
					...prev,
					createdByName: {
						...prev.createdByName,
						searchText,
					},
				}));
				break;
			case ISSUANCE_TYPE:
				setOptionsList((prev) => ({
					...prev,
					issuanceType: {
						...prev.issuanceType,
						searchText,
					},
				}));
				break;
			default:
				break;
		}
	};

	const handleSearchInputChange = async (
		searchString: string,
		type: string,
		name: string,
	) => {
		setSearchFieldTexts(type, name, searchString);
		if (searchString.length < 3) {
			handleFilterEmptyResponse(type, name, false);
			return;
		}

		const getUpdatedFilters = () => {
			if (type === CREATED_BY_TYPE) {
				return regInvAppliedFilters.map((f: any) => {
					if (f.key === JURISDICTION) {
						return {
							...f,
							key: JURISDICTION_GROUP,
						};
					}
					return f;
				});
			}
			return regInvAppliedFilters;
		};

		const headers: IRegInventoryFilterHeaders = {
			searchField:
				type === REG_SECTION_TYPE
					? [DISPLAY_ID_TYPE, REG_SECTION_TYPE]
					: [type],
			searchText: searchString,
			index:
				type === CREATED_BY_TYPE
					? `reginv-group-index-${process.env.REACT_APP_ENVIRONMENT}`
					: `reginv-theme-index-${process.env.REACT_APP_ENVIRONMENT}`,
			filter: getUpdatedFilters(),
		};

		try {
			setLoadingStates(true, type, name);
			const response: any = await getFilterItemList(headers);
			if (response?.statusCode === OK) {
				setLoadingStates(false, type, name);
				getFilterOptions(response?.responseBody, type, name);
			} else if (
				response?.statusCode === FORBIDDEN ||
				response?.statusCode === INTERNAL_SERVER_ERROR ||
				response?.statusCode === BAD_REQUEST
			) {
				handleError(type, name);
			}
		} catch {
			handleError(type, name);
		}
	};

	const handleError = (type: string, name: string) => {
		setLoadingStates(false, type, name);
		handleFilterEmptyResponse(type, name, true);
	};

	const getFilterOptions = (response: any, type: string, name: string) => {
		handleFilterEmptyResponse(type, name, true);
		response.forEach((item: IRegInventoryFilterResponse) => {
			if (item.themeName) {
				const themeObject = {
					value: name,
					label: item.themeName,
					type,
				};
				setOptionsList((prev) => ({
					...prev,
					themeName: {
						...prev.themeName,
						data: [...prev.themeName.data, themeObject],
					},
				}));
			} else if (item.sectionName) {
				const sectionObject = {
					value: name,
					label: item.sectionName,
					type,
				};
				setOptionsList((prev) => ({
					...prev,
					sectionName: {
						...prev.sectionName,
						data: [...prev.sectionName.data, sectionObject],
					},
				}));
			} else if (item.international && name === INTERNATIONA_NAME) {
				const internationalObject = {
					value: name,
					label: item.international,
					type,
				};
				setOptionsList((prev) => ({
					...prev,
					international: {
						...prev.international,
						data: [...prev.international.data, internationalObject],
					},
				}));
			} else if (item.unitedStates && name === FEDERAL_STATES_NAME) {
				const federalObject = {
					value: name,
					label: item.unitedStates,
					type,
				};
				setOptionsList((prev) => ({
					...prev,
					unitedStates: {
						...prev.unitedStates,
						data: [...prev.unitedStates.data, federalObject],
					},
				}));
			} else if (item.assignedUserName) {
				const ownerObject = {
					value: name,
					label: item.assignedUserName,
					type,
					disabled: showAssignedMeChecked === true,
				};
				setOptionsList((prev) => ({
					...prev,
					assignedUserName: {
						...prev.assignedUserName,
						data: [...prev.assignedUserName.data, ownerObject],
					},
				}));
			} else if (item.createdByName) {
				const createdByObject = {
					value: name,
					label: `${item.createdByName} (${item.count})`,
					type,
				};
				setOptionsList((prev) => ({
					...prev,
					createdByName: {
						...prev.createdByName,
						data: [...prev.createdByName.data, createdByObject],
					},
				}));
			} else if (item.issuanceType) {
				const issuanceTypeObject = {
					value: name,
					label: item.issuanceType,
					type,
				};
				setOptionsList((prev) => ({
					...prev,
					issuanceType: {
						...prev.issuanceType,
						data: [...prev.issuanceType.data, issuanceTypeObject],
					},
				}));
			} else if (item.bookTitle) {
				const regBookObject = {
					value: name,
					label: item.bookTitle,
					type,
				};
				setOptionsList((prev) => ({
					...prev,
					bookTitle: {
						...prev.bookTitle,
						data: [...prev.bookTitle.data, regBookObject],
					},
				}));
			}
		});
	};

	const handleFilterEmptyResponse = (
		type: string,
		name: string,
		searchFlag: boolean,
	) => {
		switch (type) {
			case THEME_TYPE:
				setOptionsList((prev) => ({
					...prev,
					themeName: {
						...prev.themeName,
						data: [],
						searchFlag,
					},
				}));
				break;
			case REG_SECTION_TYPE:
				setOptionsList((prev) => ({
					...prev,
					sectionName: {
						...prev.sectionName,
						data: [],
						searchFlag,
					},
				}));
				break;
			case INTERNATIONA_TYPE:
				if (name === INTERNATIONA_NAME) {
					setOptionsList((prev) => ({
						...prev,
						international: {
							...prev.international,
							data: [],
							searchFlag,
						},
					}));
					break;
				}
				if (name === FEDERAL_STATES_NAME) {
					setOptionsList((prev) => ({
						...prev,
						unitedStates: {
							...prev.unitedStates,
							data: [],
							searchFlag,
						},
					}));
					break;
				}
				break;
			case ISSUANCE_TYPE:
				setOptionsList((prev) => ({
					...prev,
					issuanceType: {
						...prev.issuanceType,
						data: [],
						searchFlag,
					},
				}));
				break;
			case OWNER_TYPE:
				setOptionsList((prev) => ({
					...prev,
					assignedUserName: {
						...prev.assignedUserName,
						data: [],
						searchFlag,
					},
				}));
				break;
			case CREATED_BY_TYPE:
				setOptionsList((prev) => ({
					...prev,
					createdByName: {
						...prev.createdByName,
						data: [],
						searchFlag,
					},
				}));
				break;
			case REG_BOOK_TYPE:
				setOptionsList((prev) => ({
					...prev,
					bookTitle: {
						...prev.bookTitle,
						data: [],
						searchFlag,
					},
				}));
				break;
			default:
				break;
		}
	};

	const updateFilterHeaders = (data: IFilterKeysResponse[]) => {
		data.forEach((res) => {
			switch (res.filterKey) {
				case THEME_TYPE:
					setOptionsList((prev) => ({
						...prev,
						themeName: {
							...prev.themeName,
							name: res.filterName,
						},
					}));
					break;
				case REG_BOOK_TYPE:
					setOptionsList((prev) => ({
						...prev,
						bookTitle: {
							...prev.bookTitle,
							name: res.filterName,
						},
					}));
					break;
				case REG_SECTION_TYPE:
					setOptionsList((prev) => ({
						...prev,
						sectionName: {
							...prev.sectionName,
							name: res.filterName,
						},
					}));
					break;
				case JURISDICTION_TYPE:
					setOptionsList((prev) => ({
						...prev,
						jurisdiction: {
							...prev.jurisdiction,
							name: res.filterName,
						},
					}));
					break;
				case ISSUANCE_TYPE:
					setOptionsList((prev) => ({
						...prev,
						issuanceType: {
							...prev.issuanceType,
							name: res.filterName,
						},
					}));
					break;
				case WORK_FLOW_STATUS_TYPE:
					setOptionsList((prev) => ({
						...prev,
						status: {
							...prev.status,
							name: res.filterName,
						},
					}));
					break;
				case OWNER_TYPE:
					setOptionsList((prev) => ({
						...prev,
						assignedUserName: {
							...prev.assignedUserName,
							name: res.filterName,
						},
					}));
					break;
				case CREATED_BY_TYPE:
					setOptionsList((prev) => ({
						...prev,
						createdByName: {
							...prev.createdByName,
							name: res.filterName,
						},
					}));
					break;
				case PRIORITY_TYPE:
					setOptionsList((prev) => ({
						...prev,
						priority: {
							...prev.priority,
							name: res.filterName,
						},
					}));
					break;
				case SOURCE_TYPE:
					setOptionsList((prev) => ({
						...prev,
						source: {
							...prev.source,
							name: res.filterName,
						},
					}));
					break;
				default:
					break;
			}
		});
	};

	const getFilterHeaders = async () => {
		const filterKeyParams = {
			dataSource: PWC,
			module: 'reginv',
		};
		setIsFilterValuesLoading(true);
		const response = await getFilterHeaderValues(filterKeyParams);
		if (response.statusCode === OK) {
			updateFilterHeaders(response.responseBody || []);
		} else {
			dispatch(
				setToastMessage({
					message: API_FAIL_ERROR_MESSAGE,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
		setIsFilterValuesLoading(false);
	};

	const options: IAdvancedFilterValueOptions[] = regInvAppliedFilters.flatMap(
		(res: any) => {
			const subOptions: IAdvancedFilterValueOptions = res.values?.map(
				(ele: any) => ({
					label: ele,
					value: res.name,
					checked: true,
					type: res.key,
				}),
			);
			return subOptions;
		},
	);

	const filterValues: IAdvancedFiltersOptions[] = [
		{
			filterKey: optionsList.bookTitle.type,
			selectedOptions: options.filter((res) => res.type === REG_BOOK_TYPE),
			filterValues: optionsList.bookTitle.data,
			filterTitle: optionsList.bookTitle.name,
			isLoading: optionsList.bookTitle.loading,
			searchText: optionsList.bookTitle.searchText,
			searchFlag: optionsList.bookTitle.searchFlag,
		},
		{
			filterKey: optionsList.themeName.type,
			selectedOptions: options.filter((res) => res.type === THEME_TYPE),
			filterValues: optionsList.themeName.data,
			filterTitle: optionsList.themeName.name,
			isLoading: optionsList.themeName.loading,
			searchText: optionsList.themeName.searchText,
			searchFlag: optionsList.themeName.searchFlag,
		},
		{
			filterKey: optionsList.sectionName.type,
			selectedOptions: options.filter((res) => res.type === REG_SECTION_TYPE),
			filterValues: optionsList.sectionName.data,
			filterTitle: optionsList.sectionName.name,
			isLoading: optionsList.sectionName.loading,
			searchText: optionsList.sectionName.searchText,
			searchFlag: optionsList.sectionName.searchFlag,
		},
		{
			filterKey: optionsList.jurisdiction.type,
			filterValues: optionsList.jurisdiction.data,
			filterTitle: optionsList.jurisdiction.name,
			subOptions: [
				{
					filterKey: optionsList.international.type,
					selectedOptions:
						options.filter(
							(res) =>
								res.type === INTERNATIONA_TYPE &&
								res.value === INTERNATIONA_NAME,
						) || [],
					filterValues: optionsList.international.data,
					filterTitle: optionsList.international.name,
					searchText: optionsList.international.searchText,
					isLoading: optionsList.international.loading,
					searchFlag: optionsList.international.searchFlag,
				},
				{
					filterKey: optionsList.unitedStates.type,
					selectedOptions: options.filter(
						(res) =>
							res.type === FEDERAL_STATES_TYPE &&
							res.value === FEDERAL_STATES_NAME,
					),
					filterValues: optionsList.unitedStates.data,
					filterTitle: optionsList.unitedStates.name,
					searchText: optionsList.unitedStates.searchText,
					isLoading: optionsList.unitedStates.loading,
					searchFlag: optionsList.unitedStates.searchFlag,
				},
			],
		},
		{
			filterKey: optionsList.issuanceType.type,
			selectedOptions: options.filter((res) => res.type === ISSUANCE_TYPE),
			filterValues: optionsList.issuanceType.data,
			filterTitle: optionsList.issuanceType.name,
			isLoading: optionsList.issuanceType.loading,
			searchText: optionsList.issuanceType.searchText,
			searchFlag: optionsList.issuanceType.searchFlag,
		},
		{
			filterKey: optionsList.status.type,
			filterValues: optionsList.status.data,
			filterTitle: optionsList.status.name,
		},
		{
			filterKey: optionsList.assignedUserName.type,
			selectedOptions: options.filter((res) => res.type === OWNER_TYPE),
			filterValues: optionsList.assignedUserName.data,
			filterTitle: optionsList.assignedUserName.name,
			isLoading: optionsList.assignedUserName.loading,
			searchText: optionsList.assignedUserName.searchText,
			searchFlag: optionsList.assignedUserName.searchFlag,
		},
		{
			filterKey: optionsList.createdByName.type,
			selectedOptions: options.filter((res) => res.type === CREATED_BY_TYPE),
			filterValues: optionsList.createdByName.data,
			filterTitle: optionsList.createdByName.name,
			isLoading: optionsList.createdByName.loading,
			searchText: optionsList.createdByName.searchText,
			searchFlag: optionsList.createdByName.searchFlag,
		},
		{
			filterKey: optionsList.priority.type,
			filterValues: optionsList.priority.data,
			filterTitle: optionsList.priority.name,
		},
		{
			filterKey: optionsList.source.type,
			filterValues: optionsList.source.data,
			filterTitle: optionsList.source.name,
		},
	];

	const advancedFilterButtonClicked = () => {
		getFilterHeaders();
		setIsAdvancedFilterOpen(true);
		dispatch(setFileDrawer(true));
	};

	const assignedToMeChanged = (assignedToMe: boolean) => {
		dispatch(setShowAssignedToMeChecked(assignedToMe));
		setOptionsList((prev) => ({
			...prev,
			assignedUserName: {
				...prev.assignedUserName,
				data: [
					...prev.assignedUserName.data.map((res) => ({
						...res,
						disabled: assignedToMe,
					})),
				],
			},
		}));
	};

	const onCloseAdvancedFilters = () => {
		setOptionsList(defaultOptionsList);
		setIsAdvancedFilterOpen(false);
		dispatch(setFileDrawer(false));
	};

	const handleFiltersApplied = (data: IAdvancedFilterValueOptions[]) => {
		const updatedData = data.map((res) => {
			if (res.type === CREATED_BY_TYPE) {
				return {
					...res,
					label: `${res.label.split(' ')[0]} ${res.label.split(' ')[1]}`,
				};
			}
			return res;
		});
		const filters = filterDataHandler(
			updatedData,
			showAssignedMeChecked,
			loggedInUser,
		);
		dispatch(setRegInventoryAppliedFilters(filters));
		onCloseAdvancedFilters();
	};

	useEffect(() => {
		applyFilterClicked();
	}, [regInvAppliedFilters]);

	const handleClearAll = () => {
		dispatch(setRegInventoryAppliedFilters([]));
	};

	return (
		<>
			<ButtonComp
				btnType="secondary"
				variant="outlined"
				onClick={advancedFilterButtonClicked}
				data-testid="filter-icon"
				compClass={
					regInvAppliedFilters.length > 0 || showAssignedMeChecked
						? styles['select-component-applied']
						: styles['select-component']
				}
			>
				<div className={styles['filter-icon']}>
					<FilterIcon
						fill={
							regInvAppliedFilters.length > 0 || showAssignedMeChecked
								? buttonTextColorApplied
								: buttonTextColor
						}
					/>
				</div>
				<Typography
					className={
						regInvAppliedFilters.length > 0 || showAssignedMeChecked
							? styles['advanced-filter-text-applied']
							: styles['advanced-filter-text']
					}
				>
					{ADVANCED_FILTERS}
				</Typography>
			</ButtonComp>
			<SideDrawer
				className={styles['data-ingestion-drawer']}
				drawerState={fileDrawerState}
				customClass={`${styles['custom-width']}`}
			>
				{isAdvancedFilterOpen && (
					<AdvancedFilters
						filterOptionsData={filterValues}
						filterDataLoading={isFilterValuesLoading}
						onClose={onCloseAdvancedFilters}
						applyFiltersHandler={handleFiltersApplied}
						handleClearFilter={handleClearAll}
						selectedListOptions={options}
						onCancel={onCloseAdvancedFilters}
						onAssignedToMeChecked={assignedToMeChanged}
						moduleType
						hideSearchFieldKeys={[
							WORK_FLOW_STATUS_TYPE,
							PRIORITY_TYPE,
							SOURCE_TYPE,
						]}
						onSearchInputChange={handleSearchInputChange}
						showingAssignedToMe={showAssignedMeChecked}
					/>
				)}
			</SideDrawer>
		</>
	);
};

export default AdvancedFilter;
